import { useSelector } from 'react-redux'

import type { IMappedGeoMappingItem } from 'partner360/components/filters'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { selectCockpitGeoMappingItems } from '../selectors'
import { IEnrichedCockpitFinSobRow } from '../types'

import { useCockpitSettings } from './useCockpitSettings'
import { useFilteredFinSobRows } from './useFilteredFinSobRows'

export interface IFinSobAggregationInputs {
  targetRows: IEnrichedCockpitFinSobRow[]
  actualsRows: IEnrichedCockpitFinSobRow[]
  quotaRows: IEnrichedCockpitFinSobRow[]
  /** The tree behind the hierarchy breakdown's Geo axis (manager cockpit mode only), joined against
   *  each row's own `countryCode`. Harmless to select unconditionally; cheap already-in-store
   *  state, and simply unused when the Geo axis is disabled. */
  geoMappingItems: IMappedGeoMappingItem[]
  selectedFYear: number
  currentFQuarter: number
  fQuarterActualsPercentage: number
  actualsType: ActualsValueTypes
  isLocalCurrency: boolean
  isYtd: boolean
  isPeriodOpen: boolean
  /** Whether the year before `selectedFYear` is still in its last quarter — see
   *  `selectIsCockpitPrevFYearClosing`. */
  isPrevFYearClosing: boolean
  currency: string
  /** The category the "total" figures should resolve to, since `targetRows`/`actualsRows`/
   *  `quotaRows` are already narrowed to it upstream — `OVERALL_CATEGORY` unless one category is
   *  selected. */
  totalCategory: string
}

/** The Redux + derived-state wiring shared by every Revenue & SOB aggregation: the filtered
 *  target/actuals/quota rows plus the currency/period settings that both the per-category
 *  breakdown and the Product hierarchy breakdown need. Extracted so `useFinSobCategoryBreakdown`
 *  and `useFinSobHierarchyBreakdown` don't each repeat this wiring. */
export const useFinSobAggregationInputs = (): IFinSobAggregationInputs => {
  const { targetRows, actualsRows, quotaRows } = useFilteredFinSobRows()
  const geoMappingItems = useSelector(selectCockpitGeoMappingItems)
  const {
    actualsType,
    isLocalCurrency,
    currency,
    selectedCategory,
    isYtd,
    isPeriodOpen,
    isPrevFYearClosing,
    selectedFYear,
    currentFQuarter,
    fQuarterActualsPercentage,
  } = useCockpitSettings()

  return {
    targetRows,
    actualsRows,
    quotaRows,
    geoMappingItems,
    selectedFYear,
    currentFQuarter,
    fQuarterActualsPercentage,
    actualsType,
    isLocalCurrency,
    isYtd,
    isPeriodOpen,
    isPrevFYearClosing,
    currency,
    totalCategory: selectedCategory,
  }
}
